/* eslint-disable camelcase */
import { ModelBuilder } from 'react-native-jala-orm/src/model/Builder';
import _ from 'lodash';
import moment from 'moment';
import Measurement from './Measurement';
import Parameter from './Parameter';
import { Platform } from "react-native";

class MultiMeasurement extends Measurement {
  static name = 'MultiMeasurement'

  _table = 'measurements';

  _fillable = [
    'id', 'pond_id', 'cycle_id', 'measured_at',
    'created_at', 'updated_at', 'deleted_at', 'synced_at',

    'ph', 'salinity', 'temperature', 'do', 'transparency',
    'water_level', 'do_percent', 'ec', 'tds', 'gravity',
    'color', 'weather', 'turbidity', 'tom', 'ammonia',
    'ammonium', 'total_ammonia', 'nitrit', 'nitrat', 'phosphate',
    'bicarbonat', 'carbonat', 'alkalinity', 'magnesium', 'calcium',
    'hardness', 'potassium', 'orp', 'ga', 'bga',
    'dino', 'diatom', 'euglenophyta', 'haptyophyta', 'gba',
    'gga', 'yga', 'floc', 'prozoo', 'zoo',
    'total_plankton', 'yellow', 'green', 'luminance', 'black',
    'total_vibrio', 'total_bacteria'
  ]

  _casts = {
    ph: 'float',
    salinity: 'float',
    temperature: 'float',
    do: 'float',
    transparency: 'float',
    water_level: 'float',
    do_percent: 'float',
    ec: 'float',
    tds: 'float',
    gravity: 'float',
    color: 'float',
    weather: 'float',
    turbidity: 'float',
    tom: 'float',
    ammonia: 'float',
    ammonium: 'float',
    total_ammonia: 'float',
    nitrit: 'float',
    nitrat: 'float',
    phosphate: 'float',
    bicarbonat: 'float',
    carbonat: 'float',
    alkalinity: 'float',
    magnesium: 'float',
    calcium: 'float',
    hardness: 'float',
    potassium: 'float',
    orp: 'float',
    ga: 'float',
    bga: 'float',
    dino: 'float',
    diatom: 'float',
    euglenophyta: 'float',
    haptyophyta: 'float',
    gba: 'float',
    gga: 'float',
    yga: 'float',
    floc: 'float',
    prozoo: 'float',
    zoo: 'float',
    total_plankton: 'float',
    yellow: 'float',
    green: 'float',
    luminance: 'float',
    black: 'float',
    total_vibrio: 'float',
    total_bacteria: 'float',
    ph_morning: 'float',
    ph_afternoon: 'float',
    temperature_morning: 'float',
    temperature_afternoon: 'float',
    do_morning: 'float',
    do_afternoon: 'float',
    ph_anomaly: 'boolean',
    salinity_anomaly: 'boolean',
    temperature_anomaly: 'boolean',
    do_anomaly: 'boolean',
    transparency_anomaly: 'boolean',
    water_level_anomaly: 'boolean',
    do_percent_anomaly: 'boolean',
    ec_anomaly: 'boolean',
    tds_anomaly: 'boolean',
    gravity_anomaly: 'boolean',
    color_anomaly: 'boolean',
    weather_anomaly: 'boolean',
    turbidity_anomaly: 'boolean',
    tom_anomaly: 'boolean',
    ammonia_anomaly: 'boolean',
    ammonium_anomaly: 'boolean',
    total_ammonia_anomaly: 'boolean',
    nitrit_anomaly: 'boolean',
    nitrat_anomaly: 'boolean',
    phosphate_anomaly: 'boolean',
    bicarbonat_anomaly: 'boolean',
    carbonat_anomaly: 'boolean',
    alkalinity_anomaly: 'boolean',
    magnesium_anomaly: 'boolean',
    calcium_anomaly: 'boolean',
    hardness_anomaly: 'boolean',
    potassium_anomaly: 'boolean',
    orp_anomaly: 'boolean',
    ga_anomaly: 'boolean',
    bga_anomaly: 'boolean',
    dino_anomaly: 'boolean',
    diatom_anomaly: 'boolean',
    euglenophyta_anomaly: 'boolean',
    haptyophyta_anomaly: 'boolean',
    gba_anomaly: 'boolean',
    gga_anomaly: 'boolean',
    yga_anomaly: 'boolean',
    floc_anomaly: 'boolean',
    prozoo_anomaly: 'boolean',
    zoo_anomaly: 'boolean',
    total_plankton_anomaly: 'boolean',
    yellow_anomaly: 'boolean',
    green_anomaly: 'boolean',
    luminance_anomaly: 'boolean',
    black_anomaly: 'boolean',
    total_vibrio_anomaly: 'boolean',
    total_bacteria_anomaly: 'boolean',
    ph_morning_anomaly: 'boolean',
    ph_afternoon_anomaly: 'boolean',
    temperature_morning_anomaly: 'boolean',
    temperature_afternoon_anomaly: 'boolean',
    do_morning_anomaly: 'boolean',
    do_afternoon_anomaly: 'boolean',

    modified: 'boolean'
  };

  static endpoint = Platform.OS === 'web' ? '/multi_measurements' : '/measurements'

  static _boot() {
    super._boot();

    this.addGlobalScope('join_farm_parameter', (query: ModelBuilder) => {
      // Do Nothing
    });

    this.addGlobalScope('to_multi', (query: ModelBuilder) => {
      query.toMulti();
    });
  }

  /**
   * @param {ModelBuilder} query
   * @return {ModelBuilder}
   */
  scopeToMulti(query: ModelBuilder) {
    if (query.getModel().constructor.name === 'Measurement') query.setModel(new MultiMeasurement());
    return super.scopeToMulti(query);
  }

  /**
   *
   * @param {ModelBuilder} query
   * @returns {Promise<boolean>}
   * @private
   */
  async _performUpdate(query: ModelBuilder) {
    // If the updating event returns false, we will cancel the update operation so
    // developers can hook Validation systems into their models and cancel this
    // operation if the model does not pass validation. Otherwise, we update.
    if (this._fireModelEvent('updating') === false) {
      return false;
    }

    // First we need to create a fresh query instance and touch the creation and
    // update timestamp on the model which are maintained by us for developer
    // convenience. Then we will just continue saving the model instances.
    if (this.usesTimestamps()) {
      this._updateTimestamps();
    }

    // Once we have run the update operation, we will fire the "updated" event for
    // this model instance. This will allow developers to hook into these after
    // models are updated, giving them a chance to do any special processing.
    const dirty = this.getDirty();

    if (!_.isEmpty(dirty)) {
      await this._updateMultiple();

      this._fireModelEvent('updated', false);

      this.syncChanges();
    }

    return true;
  }

  /**
   *
   * @param {ModelBuilder} query
   * @returns {boolean}
   * @private
   */
  async _performInsert(query: ModelBuilder) {
    if (this._fireModelEvent('creating') === false) {
      return false;
    }

    // First we'll need to create a fresh query instance and touch the creation and
    // update timestamps on this model, which are maintained by us for developer
    // convenience. After, we will just continue saving these model instances.
    if (this.usesTimestamps()) {
      this._updateTimestamps();
    }

    // If the model has an incrementing key, we can use the "insertGetId" method on
    // the query builder, which will give us back the final inserted ID for this
    // table from the database. Not all tables have to be incrementing though.
    const attributes = this.getAttributes();

    if (this.getIncrementing()) await this._insertAndSetId(query, attributes);
    // If the table isn't incrementing we'll simply insert these attributes as they
    // are. These attribute arrays must contain an "id" column previously placed
    // there by the developer as the manually determined key for these models.
    else {
      if (_.isEmpty(attributes)) {
        return true;
      }

      await this._saveMultiple();
    }

    // We will go ahead and set the exists property to true, so that it is set when
    // the created event is fired, just in case the developer tries to update it
    // during the event. This will allow them to do so and run an update here.
    this.exists = true;

    this.wasRecentlyCreated = true;

    this._fireModelEvent('created', false);

    return true;
  }

  /**
   *
   * @param {ModelBuilder} query
   * @param {Object} attributes
   * @private
   */
  async _insertAndSetId(query: ModelBuilder, attributes) {
    const keyName = this.getKeyName();
    const id = await this._saveMultiple();

    this.setAttribute(keyName, id);
  }

  /**
   * Perform the actual delete query on this model instance.
   *
   * @return void
   * @throws Exception
   */
  async _performDeleteOnModel() {
    await this._deleteMultiple();

    this.exists = false;
  }

  /**
   * @return mixed|null
   * @throws Exception
   */
  async _saveMultiple() {
    const ids = [];
    let newId = moment().valueOf();
    await Promise.all(
      this.constructor.parameters.map((parameter: Parameter) => (async () => {
        const measurement = (await Measurement.query()
          .where(new Measurement().qualifyColumn('pond_id'), this.pond_id)
          .where(
            new Measurement().qualifyColumn('parameter_id'),
            parameter.getKey(),
          )
          .where(
            new Measurement().qualifyColumn('measured_at'),
            this.measured_at,
          )
          .first()) || new Measurement();
        if (!measurement.exists && !this[parameter.code]) return;
        if (measurement.exists && !this.isDirty(parameter.code)) {
          ids.push(measurement.getKey());
        } else if (
          measurement.exists
            && this.isDirty(parameter.code)
            && !this[parameter.code]
        ) {
          await measurement.delete();
        } else {
          if (!measurement.getKey()) {
            measurement.id = -newId;
            newId += 1;
          }
          measurement.cycle_id = this.cycle_id;
          measurement.pond_id = this.pond_id;
          measurement.parameter_id = parameter.getKey();
          measurement.measured_at = this.measured_at;
          measurement.value = this[parameter.code];
          await measurement.save();
          ids.push(measurement.getKey());
        }
      })(),),
    );
    return !ids.length ? this.getKey() : _.min(ids);
  }

  /**
   * @return mixed|null
   * @throws Exception
   */
  async _updateMultiple() {
    let multiMeasurement: MultiMeasurement = null;
    const measuredAtDirty = this.isDirty(['measured_at']);
    if (measuredAtDirty) {
      multiMeasurement = await MultiMeasurement.query().find(this.getKey());
    }
    this.setAttribute(this.getKeyName(), await this._saveMultiple());
    this.syncOriginalAttribute(this.getKeyName());
    if (measuredAtDirty && multiMeasurement) await multiMeasurement._deleteMultiple();
    return this.getKey();
  }

  /**
   * @throws Exception
   */
  async _deleteMultiple() {
    await Promise.all(
      this.constructor.parameters.map((parameter: Parameter) => (async () => {
        if (this[parameter.code] == null) return;
        const measurement = (await Measurement.query()
          .where(new Measurement().qualifyColumn('pond_id'), this.pond_id)
          .where(
            new Measurement().qualifyColumn('parameter_id'),
            parameter.getKey(),
          )
          .where(
            new Measurement().qualifyColumn('measured_at'),
            this.measured_at,
          )
          .first()) || new Measurement();
        if (measurement.exists) await measurement.delete();
      })(),),
    );
  }

  /**
   * @returns {SyncableModel|null}
   */
  async push() {
    return Measurement.query().withoutGlobalScope('soft_delete')
      .whereNotNull(new Measurement().qualifyColumn('deleted_at'))
      .get()
      .then((measurements: Array) => Promise.all(
        measurements.map((measurement: Measurement) => measurement.push())
      ))
      .catch()
      .then(() => Measurement.pushAll([
        ['measurements.pond_id', [this.pond_id]],
        ['measurements.measured_at', [this.measured_at]],
      ], true, undefined, undefined, true));
  }

  static fetch(params = {}) {
    if (Platform.OS === 'web')
      return this.createAxios().get(this.endpoint, {
        params: {
          per_page: 1000,
          ...params
        }
      }).then(({ data: { data } }) => {
        const synced_at = moment.utc().format('YYYY-MM-DD HH:mm:ss')
        const modified = false
        return data.map((datum) => new this().newInstance({ ...datum, synced_at, modified }, true).syncOriginal())
      })
    else {
      return super.fetch(params)
    }
  }
}

export default MultiMeasurement;
